//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import * as Css from 'csstype';
import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import BulkSelectionLayer from '@components/atoms/media/BulkSelectionLayer';
import {
  MetadataItemDetails,
  MetadataLayer,
} from '@components/atoms/media/MetadataLayer';
import VideoLayer, { IPlayButton } from '@components/atoms/media/VideoLayer';
import { IMediaTypeOverlayIconProps } from '@components/atoms/overlay-icon/MediaTypeOverlayIcon';
import { useIsElementVisible } from '@hooks/useIsElementVisible';
import { usePrevious } from '@hooks/usePrevious';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import ItemId from '@models/ItemId';
import { UniversalItemType } from '@models/UniversalItemType';
import { Mixins } from '@theme/mixins';
import { IMediaTrackableComponent } from '@tracker/mediaTracking/mediaTrackingModel';
import { withTracking } from '@utils/withTracking';

const defaultBuildTrackingId = (itemType: UniversalItemType, id: ItemId) =>
  `media_${itemType}_${id}`;

interface ILayerConfig {
  enabled: boolean;
}

interface IVideoLayerConfig extends ILayerConfig {
  backgroundColor?: string;
  controlsList?: string;
  coverImageUrl?: string;
  overlayContent?: React.ReactNode;
  playButton?: IPlayButton;
  shouldPlayOnHover?: boolean;
  thumbnailOffsetSeconds?: number;
  videoComponent?: React.ReactNode;
  videoUrl?: string;
}

interface IMetadataLayerConfig extends ILayerConfig {
  isCopyrighted?: IMediaTypeOverlayIconProps['isCopyrighted'];
  isCaptionInProgress?: boolean;
  itemDetails?: Partial<MetadataItemDetails>;
  mediaType?: IMediaTypeOverlayIconProps['type'];
  onUiaClose?: (response: IUiaModalClosedResponse) => void;
}

interface IBulkSelectionLayerConfig extends ILayerConfig {
  bulkSelectionTrackingId?: string;
}

export interface Props
  extends React.HTMLAttributes<HTMLDivElement>,
    IMediaTrackableComponent {
  imageUrl?: string;
  videoLayer?: IVideoLayerConfig;
  metadataLayer?: IMetadataLayerConfig;
  actionLayer?: (isMediaHovered: boolean) => React.ReactElement;
  bulkSelectionLayer?: IBulkSelectionLayerConfig;
  hasError?: boolean;
  itemType: UniversalItemType;
  id: ItemId;
  objectFit?: Css.Property.ObjectFit;
  includeTopBottomBorder?: boolean;
  trackingId?: string;
  onItemClick?: (event: React.MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  backgroundColor?: string;
}

/**
 * The Media atom displays a network item's image in
 * a container whose dimensions must be set by the
 * consumer.
 *
 * The atom uses a layer-based model to render content
 * on top of the image. Most overlay content is built-in
 * to the component, but some must be provided by the
 * developer on a case-by-case basis. These layers are
 * configured via layer-specific props.
 *
 * @param videoLayer Renders a <video> element that covers the
 * underlying image. Can be used to render a single frame to
 * match an underlying piece of media's poster image, or can
 * play the video inline on hover.
 *
 * @param metadataLayer Renders a media type icon in the top
 * right, and other metadata icons in the bottom left e.g.
 * "product count", "distributed to", "rights status"
 *
 * @param actionLayer This is provided by the developer and can
 * contain overlay components in the top right for secondary
 * actions, the center for primary actions, or the bottom right
 * for anything else.
 *
 * @param bulkSelectionLayer Renders a bulk action select button
 * in the top left that, when clicked, will hide the user-provided
 * actionLayer and enable the bulk selection workflow. This involves
 * adding a zoom icon in the top right to open UIA, and binds the bulk
 * select action to click events on the image itself
 *
 * @param hasError If true, renders a red border around the media.
 *
 * @example
 *
 * // A Media atom with all the bells and whistles
 * <Media
 *   imageUrl={ imageUrl }
 *   itemType={ itemType }
 *   id={ id }
 *   hasError={ true }
 *   videoLayer={ {
 *     enabled: true,
 *     videoUrl,
 *     shouldPlayOnHover: false,
 *   } }
 *   metadataLayer={ {
 *     enabled: true,
 *     mediaType,
 *   } }
 *   actionLayer={ <CustomPrimaryAction /> }
 *   bulkSelectionLayer={ {
 *     enabled: true,
 *   } }
 * />
 *
 * // A media atom that only has the bulk selection layer
 * <Media
 *   imageUrl={ imageUrl }
 *   itemType={ itemType }
 *   id={ id }
 *   videoLayer={ { enabled: false } }
 *   metadataLayer={ { enabled: false } }
 *   bulkSelectionLayer={ { enabled: true } }
 * />
 */
const Media = ({
  className = '',
  objectFit = 'contain',
  mediaTrackingConfig,
  videoLayer = {
    enabled: false,
  },
  metadataLayer = {
    enabled: true,
    mediaType: 'image',
  },
  actionLayer,
  bulkSelectionLayer = {
    enabled: true,
  },
  hasError = false,
  imageUrl,
  itemType,
  id,
  includeTopBottomBorder = false,
  onItemClick,
  onMouseEnter,
  onMouseLeave,
  backgroundColor,
  trackingId = defaultBuildTrackingId(itemType, id),
}: Props) => {
  const isTrackingEnabled =
    mediaTrackingConfig?.isMediaTrackingEnabled ?? false;

  const [isItemHovered, setIsItemHovered] = useState(false);
  const [shouldShowMetadataLayer, setShouldShowMetadataLayer] = useState(true);

  const isProduct = itemType === UniversalItemType.Product;

  const wrapperRef: React.MutableRefObject<null> = useRef(null);

  const isVisible = useIsElementVisible(wrapperRef, isTrackingEnabled);

  // This determines if the element was previously visible, using this to check that we are not resending a event on rerender/remounting of component
  const prevIsVisible = usePrevious(isVisible);

  // Using UseLayout Effect to check condition before browser repaints screen and condition potentially changes
  // Only tracking isVisible because we never want other dependency changes to fire the tracking function
  useLayoutEffect(() => {
    if (!prevIsVisible && isVisible) {
      mediaTrackingConfig?.trackVisibility();
    }
  }, [isVisible]);

  const handleMouseEnter = (event: React.MouseEvent) => {
    setIsItemHovered(true);
    onMouseEnter?.(event);
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    setIsItemHovered(false);
    onMouseLeave?.(event);
  };

  // Note that the .react-universal-image-actions, data-item-type,
  // and data-item-id classes and attributes maintain compatibility
  // with curabugger.
  return (
    <MediaWrapper
      ref={wrapperRef}
      backgroundColor={backgroundColor}
      className={
        metadataLayer.itemDetails?.muteStatus ||
        metadataLayer.isCaptionInProgress ||
        metadataLayer.itemDetails?.captionStatus
          ? `${className} react-universal-image-actions videoUrl `
          : `${className} react-universal-image-actions`
      }
      data-item-id={id}
      data-item-type={itemType}
      hasError={hasError}
      isProduct={isProduct}
      onClick={(e) => onItemClick?.(e)}
      trackingId={trackingId}
    >
      {imageUrl && (
        <Image
          includeTopBottomBorder={includeTopBottomBorder}
          objectFit={objectFit}
          src={imageUrl}
        />
      )}
      <MediaOverlay
        clickable={!!onItemClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Video Layer */}
        {videoLayer.enabled && videoLayer.videoUrl ? (
          <VideoLayer
            backgroundColor={videoLayer.backgroundColor}
            controlsList={videoLayer.controlsList}
            coverImageUrl={videoLayer.coverImageUrl}
            isMediaHovered={isItemHovered}
            objectFit={objectFit}
            overlayContent={videoLayer.overlayContent}
            playButton={videoLayer.playButton}
            setIsVideoPlaying={setShouldShowMetadataLayer}
            shouldPlayOnHover={videoLayer.shouldPlayOnHover}
            thumbnailOffsetSeconds={videoLayer.thumbnailOffsetSeconds}
            videoComponent={videoLayer.videoComponent}
            videoUrl={videoLayer.videoUrl}
          />
        ) : null}

        {/* Metadata Layer */}
        {metadataLayer.enabled && shouldShowMetadataLayer ? (
          <MetadataLayer
            id={id}
            isCaptionInProgress={metadataLayer.isCaptionInProgress}
            isCopyrighted={metadataLayer.isCopyrighted}
            itemDetails={metadataLayer.itemDetails}
            itemType={itemType}
            mediaType={metadataLayer.mediaType}
            onUiaClose={metadataLayer.onUiaClose}
          />
        ) : null}

        {/* Bulk Selection Layer + Action Layer */}
        {!bulkSelectionLayer.enabled ? (
          actionLayer?.(isItemHovered)
        ) : (
          <BulkSelectionLayer
            actionLayer={actionLayer}
            id={id}
            isMediaHovered={isItemHovered}
            itemType={itemType}
            mediaTrackingConfig={mediaTrackingConfig}
            trackingId={bulkSelectionLayer.bulkSelectionTrackingId}
          />
        )}
      </MediaOverlay>
    </MediaWrapper>
  );
};

export const MediaWithoutBulkSelect = (
  props: Omit<Props, 'bulkSelectionLayer'>,
) => <Media bulkSelectionLayer={{ enabled: false }} {...props} />;

export const MediaWithoutMetadata = (props: Omit<Props, 'metadataLayer'>) => (
  <Media metadataLayer={{ enabled: false }} {...props} />
);

export const MediaWithoutVideo = (props: Omit<Props, 'videoLayer'>) => (
  <Media videoLayer={{ enabled: false }} {...props} />
);

export const MediaWithoutLayers = (
  props: Omit<Props, 'videoLayer' | 'metadataLayer' | 'bulkSelectionLayer'>,
) => (
  <Media
    bulkSelectionLayer={{ enabled: false }}
    metadataLayer={{ enabled: false }}
    videoLayer={{ enabled: false }}
    {...props}
  />
);

const MediaOverlay = styled.div<{ clickable: boolean }>(
  ({ clickable, theme }) => css`
    cursor: ${clickable ? 'pointer' : 'unset'};
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  `,
);

const MediaWrapper = withTracking(
  styled.div<{
    backgroundColor?: string;
    hasError: boolean;
    isProduct: boolean;
  }>(({ backgroundColor, hasError, isProduct, theme }) => {
    const background = (() => {
      if (isProduct) {
        return theme.colors.white;
      }
      return backgroundColor ?? theme.colors.gray0;
    })();
    return css`
      background: ${background};
      position: relative;
      height: 100%;
      width: 100%;
      ${hasError && Mixins.Border.MediaError}
    `;
  }),
);

const Image = styled.img<{
  objectFit: Css.Property.ObjectFit;
  includeTopBottomBorder: boolean;
}>(
  ({ includeTopBottomBorder, objectFit, theme }) => css`
    width: 100%;
    height: 100%;
    object-fit: ${objectFit};
    ${includeTopBottomBorder &&
    `border: 1px ${theme.colors.gray2}; border-style: solid none;`}
  `,
);

export default Media;
